import { useEffect, useState } from 'react'
import { api } from '../api/client'
import type { RegolaPercorsoSoftware } from '../api/types'
import { useAsync } from '../hooks/useAsync'
import { t } from '../i18n/lingua'
import { Card, ErrorBanner, Spinner } from './common'

/**
 * «Percorsi software personalizzati»: the tenant's rules for finding
 * installed software where the agent's built-in detection does not look
 * (software_paths_service on the Control Plane, internal/custompaths on the
 * agent). Saved for the whole tenant; every agent receives them with its
 * settings and applies them at each software inventory.
 */

// Examples of paths and patterns: technical text, the same in every language.
const ESEMPIO_CARTELLA = '/opt/acme/*/lib · D:\\Apps · %ProgramFiles%\\Acme'
const ESEMPIO_MODELLO = '*.jar · version.txt · app-*'

const VUOTA: RegolaPercorsoSoftware = { label: '', os: '', path: '', pattern: '', kind: 'file', regex: '', name: '', vendor: '' }

function etichettaTipo(kind: string): string {
  if (kind === 'file') return t('Nome del file')
  if (kind === 'content') return t('Contenuto del file')
  if (kind === 'folder') return t('Nome della cartella')
  return t('Cartella come applicazione (SBOM)')
}

export function PercorsiSoftware({ puoModificare }: { puoModificare: boolean }) {
  const dati = useAsync(() => api.softwarePaths(), [])
  const [regole, setRegole] = useState<RegolaPercorsoSoftware[]>([])
  const [modificato, setModificato] = useState(false)
  const [busy, setBusy] = useState(false)
  const [errore, setErrore] = useState<unknown>(null)
  const [salvato, setSalvato] = useState(false)

  useEffect(() => {
    if (dati.data) {
      setRegole(dati.data.rules)
      setModificato(false)
    }
  }, [dati.data])

  function cambia(i: number, campo: keyof RegolaPercorsoSoftware, valore: string | number) {
    setRegole((rr) => rr.map((r, j) => (j === i ? { ...r, [campo]: valore } : r)))
    setModificato(true)
    setSalvato(false)
  }

  async function salva() {
    setBusy(true)
    setErrore(null)
    try {
      const r = await api.saveSoftwarePaths(regole)
      setRegole(r.rules)
      setModificato(false)
      setSalvato(true)
    } catch (err) {
      setErrore(err)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card title={t('Percorsi software personalizzati')}>
      <p className="field__hint" style={{ marginTop: 0 }}>
        {t('Definisce percorsi aggiuntivi in cui l’agente ricerca software installato. Per ogni regola specifica percorso, pattern di file o cartelle e dati da acquisire; un’espressione regolare può estrarre nome, versione e produttore. Le regole valgono per tutti gli agenti del tenant e per gli inventari software. Le cartelle classificate come applicazione vengono incluse nello SBOM, se abilitato.')}
      </p>
      <ErrorBanner error={errore ?? dati.error} />
      {salvato && <div className="banner banner--info">{t('Salvate: gli agenti le ricevono al prossimo contatto.')}</div>}
      {dati.loading && !dati.data ? (
        <Spinner />
      ) : (
        <>
          {regole.length === 0 && <p className="text-muted">{t('Nessuna regola: l’agente usa solo la ricerca integrata.')}</p>}
          {regole.map((r, i) => (
            <fieldset key={r.id ?? `nuova-${i}`} className="card__inset" style={{ marginBottom: 8 }} disabled={!puoModificare}>
              <div className="grid grid--2">
                <div className="field">
                  <label>{t('Nome della regola')}</label>
                  <input value={r.label ?? ''} onChange={(e) => cambia(i, 'label', e.target.value)} placeholder={t('es. Gestionale Acme')} />
                </div>
                <div className="field">
                  <label>{t('Sistema')}</label>
                  <select value={r.os ?? ''} onChange={(e) => cambia(i, 'os', e.target.value)}>
                    <option value="">{t('Tutti')}</option>
                    <option value="windows">Windows</option>
                    <option value="linux">Linux</option>
                    <option value="darwin">{'macOS'}</option>
                  </select>
                </div>
                <div className="field">
                  <label>{t('Cartella')}</label>
                  <input className="mono" value={r.path} onChange={(e) => cambia(i, 'path', e.target.value)} placeholder={ESEMPIO_CARTELLA} />
                </div>
                <div className="field">
                  <label>{t('Modello dei nomi')}</label>
                  <input className="mono" value={r.pattern ?? ''} onChange={(e) => cambia(i, 'pattern', e.target.value)} placeholder={ESEMPIO_MODELLO} />
                </div>
                <div className="field">
                  <label>{t('Cosa leggere')}</label>
                  <select value={r.kind} onChange={(e) => cambia(i, 'kind', e.target.value)}>
                    {['file', 'content', 'folder', 'sbom'].map((k) => <option key={k} value={k}>{etichettaTipo(k)}</option>)}
                  </select>
                </div>
                <div className="field">
                  <label>{t('Profondità')}</label>
                  <input type="number" min={1} max={6} value={r.depth ?? ''} placeholder="2"
                    onChange={(e) => cambia(i, 'depth', Number(e.target.value) || 0)} />
                </div>
                {r.kind !== 'sbom' && (
                  <div className="field" style={{ gridColumn: '1 / -1' }}>
                    <label>{t('Espressione regolare')}</label>
                    <input className="mono" value={r.regex ?? ''} onChange={(e) => cambia(i, 'regex', e.target.value)}
                      placeholder={r.kind === 'content' ? '(?m)^Version:\\s*(?P<version>\\S+)' : '^(?P<name>.+)-(?P<version>\\d[\\w.]*)\\.jar$'} />
                  </div>
                )}
                <div className="field">
                  <label>{t('Nome del prodotto (fisso)')}</label>
                  <input value={r.name ?? ''} onChange={(e) => cambia(i, 'name', e.target.value)} />
                </div>
                {r.kind !== 'sbom' && (
                  <div className="field">
                    <label>{t('Produttore (fisso)')}</label>
                    <input value={r.vendor ?? ''} onChange={(e) => cambia(i, 'vendor', e.target.value)} />
                  </div>
                )}
              </div>
              {puoModificare && (
                <button className="linklike" onClick={() => { setRegole((rr) => rr.filter((_, j) => j !== i)); setModificato(true) }}>
                  {t('Togli la regola')}
                </button>
              )}
            </fieldset>
          ))}
          {puoModificare && (
            <div className="rowactions" style={{ textAlign: 'left' }}>
              <button className="secondary" onClick={() => { setRegole((rr) => [...rr, { ...VUOTA }]); setModificato(true) }}>
                {t('Aggiungi una regola')}
              </button>{' '}
              <button onClick={() => void salva()} disabled={busy || !modificato}>
                {busy ? t('Salvataggio…') : t('Salva')}
              </button>
            </div>
          )}
        </>
      )}
    </Card>
  )
}
